import { useState, useEffect, useCallback, RefObject } from 'react';

/** Bounding box coordinates for graph nodes */
interface BoundingBox {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

/** Pan offset coordinates */
interface PanOffset {
  x: number;
  y: number;
}

/** Pan start coordinates */
interface PanStart {
  x: number;
  y: number;
}

/** Node position for bounding box calculation */
interface NodePosition {
  x: number;
  y: number;
}

/**
 * Custom hook for managing graph viewport state including zoom, pan, and fit-to-window.
 * Handles mouse wheel zooming, keyboard shortcuts, and panning interactions.
 */
export function useGraphViewport(
  containerRef: RefObject<HTMLDivElement>,
  nodes: NodePosition[]
) {
  const [scale, setScale] = useState(1);
  const [panOffset, setPanOffset] = useState<PanOffset>({ x: 50, y: 50 });
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState<PanStart>({ x: 0, y: 0 });

  /**
   * Calculate bounding box of all nodes in the graph.
   * Returns default bounds if no nodes exist.
   */
  const getBoundingBox = useCallback((): BoundingBox => {
    if (nodes.length === 0) {
      return { minX: 0, minY: 0, maxX: 1000, maxY: 500 };
    }
    const minX = Math.min(...nodes.map(n => n.x));
    const minY = Math.min(...nodes.map(n => n.y));
    const maxX = Math.max(...nodes.map(n => n.x + 180));
    const maxY = Math.max(...nodes.map(n => n.y + 80));
    return { minX, minY, maxX, maxY };
  }, [nodes]);

  /** Zoom in by 0.25, max 2x */
  const zoomIn = useCallback(() => {
    setScale(prev => Math.min(prev + 0.25, 2));
  }, []);

  /** Zoom out by 0.25, min 0.1x */
  const zoomOut = useCallback(() => {
    setScale(prev => Math.max(prev - 0.25, 0.1));
  }, []);

  /**
   * Fit all nodes to the viewport with padding.
   * Calculates optimal scale and pan offset to show all nodes.
   */
  const fitToWindow = useCallback(() => {
    if (!containerRef.current) return;
    const container = containerRef.current.getBoundingClientRect();
    const bbox = getBoundingBox();
    const padding = 100;
    const graphWidth = bbox.maxX - bbox.minX + padding * 2;
    const graphHeight = bbox.maxY - bbox.minY + padding * 2;
    const scaleX = container.width / graphWidth;
    const scaleY = container.height / graphHeight;
    const newScale = Math.min(scaleX, scaleY, 1);
    setScale(newScale);
    setPanOffset({
      x: (container.width / newScale - (bbox.maxX + bbox.minX)) / 2,
      y: (container.height / newScale - (bbox.maxY + bbox.minY)) / 2
    });
  }, [containerRef, getBoundingBox]);

  /**
   * Pan to center a specific node in the viewport.
   * @param nodeX - Node x coordinate
   * @param nodeY - Node y coordinate
   */
  const panToNode = useCallback((nodeX: number, nodeY: number) => {
    if (!containerRef.current) return;
    const containerWidth = containerRef.current.clientWidth;
    const containerHeight = containerRef.current.clientHeight;
    setPanOffset({
      x: -nodeX + containerWidth / 2 / scale - 90,
      y: -nodeY + containerHeight / 2 / scale - 40
    });
  }, [containerRef, scale]);

  /** Mouse wheel zoom with Ctrl/Cmd modifier */
  useEffect(() => {
    const handleWheel = (e: WheelEvent) => {
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
        const delta = e.deltaY > 0 ? -0.1 : 0.1;
        setScale(prev => Math.max(0.1, Math.min(2, prev + delta)));
      }
    };
    const container = containerRef.current;
    if (container) {
      container.addEventListener('wheel', handleWheel, { passive: false });
      return () => container.removeEventListener('wheel', handleWheel);
    }
  }, [containerRef]);

  /** Keyboard shortcuts for zoom and fit */
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey) {
        if (e.key === '=' || e.key === '+') {
          e.preventDefault();
          zoomIn();
        } else if (e.key === '-') {
          e.preventDefault();
          zoomOut();
        } else if (e.key === '0') {
          e.preventDefault();
          fitToWindow();
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [zoomIn, zoomOut, fitToWindow]);

  /** Pan handlers for mouse drag */
  const handleMouseDown = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    if (target.closest('.task-node') || target.closest('button') || target.closest('.minimap')) {
      return;
    }
    if (e.button === 0) {
      setIsPanning(true);
      setPanStart({ x: e.clientX, y: e.clientY });
    }
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (isPanning) {
      const deltaX = e.clientX - panStart.x;
      const deltaY = e.clientY - panStart.y;
      setPanOffset(prev => ({
        x: prev.x + deltaX / scale,
        y: prev.y + deltaY / scale
      }));
      setPanStart({ x: e.clientX, y: e.clientY });
    }
  };

  const handleMouseUp = () => {
    setIsPanning(false);
  };

  return {
    scale,
    panOffset,
    isPanning,
    zoomIn,
    zoomOut,
    fitToWindow,
    panToNode,
    setPanOffset,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
  };
}
